<template>
  <div class="f fs gap8 like-and-share">
    <button
      :class="{ liked: userLiked }"
      :disabled="loading"
      class="btn btn-primary btn-like"
      type="button"
      @click.stop="likePost"
    >
      <svg
        height="16"
        width="16"
      >
        <use xlink:href="/icons/sprite.svg#i-heart" />
      </svg>
      <span class="f14">{{ likesCount }}</span>
    </button>
    <div
      v-click-outside="() => optionsShow = false"
      :class="{ opened: optionsShow }"
      class="btn-wrapper"
    >
      <button
        class="btn btn-violet"
        @click="optionsShow = !optionsShow"
      >
        <svg
          class="mr8"
          height="16"
          width="16"
        >
          <use xlink:href="/icons/sprite.svg#i-share" />
        </svg>
        <span class="f14">{{ $t('share') }}</span>
      </button>

      <div class="actions-list">
        <div
          class="actions-list-item top"
          @click="onCopied"
        >
          <span>{{ $t('share_options') }}</span>
        </div>
        <button
          class="actions-list-item"
          @click="onCopied"
        >
          <svg
            height="16"
            width="16"
          >
            <use xlink:href="/icons/sprite.svg#i-link" />
          </svg>
          <span class="f14">Copy link</span>
        </button>
        <button
          class="actions-list-item"
          @click="shareToTwitter"
        >
          <svg
            height="16"
            width="16"
          >
            <use xlink:href="/icons/sprite.svg#i-social-x" />
          </svg>
          <span class="f14">X</span>
        </button>
        <button
          class="actions-list-item"
          @click="shareToTelegram"
        >
          <svg
            height="16"
            width="16"
          >
            <use xlink:href="/icons/sprite.svg#i-telegram" />
          </svg>
          <span class="f14">Telegram</span>
        </button>
        <button
          class="actions-list-item"
          @click="onCopied"
        >
          <svg
            height="16"
            width="16"
          >
            <use xlink:href="/icons/sprite.svg#i-instagram" />
          </svg>
          <span class="f14">Instagram</span>
        </button>
        <button
          class="actions-list-item"
          @click="shareToReddit"
        >
          <svg
            height="16"
            width="16"
          >
            <use xlink:href="/icons/sprite.svg#i-reddit" />
          </svg>
          <span class="f14">Reddit</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { EventBus } from '~/composables/bus';
import { setBlogLike } from '~/utils/api';

const props = defineProps<{
  likesCount: number
  userLiked: string | boolean | null
}>();
const emit = defineEmits(['onLiked']);

const optionsShow = ref(false);
const loading = ref(false);

function onCopied() {
  optionsShow.value = false;
  navigator.clipboard.writeText(window.location.href).then(() => {
    EventBus.$emit('notify', {
      success: true,
      msg: 'Copied to clipboard',
    });
  }).catch(() => {
    EventBus.$emit('notify', {
      success: false,
      msg: 'Something went wrong',
    });
  });
}

function shareToTelegram() {
  const url = encodeURIComponent(window.location.href);
  window.open(`https://t.me/share/url?url=${url}`, '_blank');
}

function shareToTwitter() {
  const url = encodeURIComponent(window.location.href);
  window.open(`https://twitter.com/intent/tweet?url=${url}`, '_blank');
}

function shareToReddit() {
  const url = encodeURIComponent(window.location.href);
  window.open(`https://www.reddit.com/submit?url=${url}`, '_blank');
}

async function likePost() {
  const route = useRoute();
  loading.value = true;
  try {
    const { errors } = await setBlogLike(route.params.link as string);
    if (errors?.length) {
      throw new Error(errors[0].message);
    }

    EventBus.$emit('notify', {
      success: true,
      msg: !props.userLiked ? 'Post liked' : 'Post unliked',
    });
    emit('onLiked', !props.userLiked);
  } catch (e: any) {
    EventBus.$emit('notify', {
      success: false,
      msg: e.message,
    });
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped>
.like-and-share {
  button {
    svg {
      width: 24px;
      height: 24px;
    }
  }

  .btn-like {
    color: var(--accent-secondary);

    svg {
      color: var(--surfaces-on-surface-variable);
    }

    &.liked {
      svg {
        color: var(--destructive);
        fill: var(--destructive);
      }
    }
  }

  .btn-wrapper {
    position: relative;
    z-index: 5;

    &.opened .actions-list {
      display: flex;
      flex-direction: column;
    }

    .actions-list {
      display: none;
      position: absolute;
      left: 0;
      top: 110%;
      width: max-content;
      min-width: 200px;
      background: var(--accent-on-primary);
      border-radius: 8px;
      border: 1px solid var(--surfaces-high-container);
      overflow: hidden;

      &-item {
        padding: 12px;
        display: flex;
        justify-content: flex-start;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;

        &.top {
          color: var(--accent-on-surface);
        }

        &:not(.top):hover {
          background: var(--surfaces-high-container);
        }
      }
    }
  }
}
</style>
